import { useMemo, useState } from 'react';
import {
    Box, Typography, Stack, IconButton, Dialog, DialogTitle, DialogContent,
    DialogActions, Button, CircularProgress, Alert, TextField, MenuItem,
    Rating, Tooltip, FormControlLabel, Switch, Chip,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';


type AdminReview = {
    userId: string;
    placeId: string;
    placeAddress: string;
    placeName: string | null;
    authorEmail: string | null;
    authorName: string | null;
    star: number;
    description: string | null;
    /** Teksta moderācijas statuss - null = pending, true = apstiprināts, false = noraidīts. */
    accepted: boolean | null;
    deleted: boolean;
    createdAt: string;
    deletedAt: string | null;
};


export function ReviewsPage() {
    const queryClient = useQueryClient();
    const [filters, setFilters] = useState<{
        placeId: string; userId: string; minStars: string; search: string; includeDeleted: boolean;
    }>({ placeId: '', userId: '', minStars: '', search: '', includeDeleted: false });
    const [confirmDelete, setConfirmDelete] = useState<AdminReview | null>(null);

    const { data: reviews, isLoading, error } = useQuery({
        queryKey: ['admin-reviews', filters],
        queryFn: async () => {
            const resp = await api.get<{ items: AdminReview[] }>('/api/v1/admin/reviews', {
                params: {
                    placeId: filters.placeId || undefined,
                    userId: filters.userId || undefined,
                    minStars: filters.minStars || undefined,
                    search: filters.search || undefined,
                    includeDeleted: filters.includeDeleted || undefined,
                },
            });
            return resp.data.items;
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (r: AdminReview) => {
            await api.delete('/api/v1/admin/reviews', {
                params: { userId: r.userId, placeId: r.placeId },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-reviews'] });
            setConfirmDelete(null);
        },
    });

    // Teksta moderācijas lēmums - to pašu endpointu izmanto arī pending queue lapa.
    // Autoram tiek nosūtīts push paziņojums; refetch, lai statuss uzreiz atjaunotos.
    const decideMutation = useMutation({
        mutationFn: async (args: { userId: string; placeId: string; accepted: boolean }) => {
            const resp = await api.post<{ isSuccess: boolean; error: string | null }>(
                '/api/v1/admin/moderation/reviews/decide',
                args,
            );
            if (!resp.data.isSuccess) throw new Error(resp.data.error || 'Lēmums neizdevās.');
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-reviews'] }),
    });

    const columns = useMemo<GridColDef<AdminReview>[]>(() => [
        {
            field: 'createdAt', headerName: 'Datums', width: 130,
            valueFormatter: (v?: string) => v
                ? new Date(v).toLocaleDateString('lv-LV')
                : '',
        },
        {
            field: 'star', headerName: 'Vērtējums', width: 140, sortable: true,
            renderCell: (p) => <Rating value={p.value as number} size="small" readOnly />,
        },
        {
            field: 'description', headerName: 'Atsauksme', flex: 1.5, minWidth: 240, sortable: false,
            renderCell: (p) => p.value
                ? <Tooltip title={p.value as string} placement="top-start">
                      <span style={{
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          overflow: 'hidden', lineHeight: 1.3, paddingTop: 4, paddingBottom: 4,
                      }}>{p.value as string}</span>
                  </Tooltip>
                : <span style={{ color: '#888' }}>—</span>,
        },
        {
            field: 'place', headerName: 'Vieta', flex: 1, minWidth: 200, sortable: false,
            renderCell: (p) => (
                <Stack sx={{ lineHeight: 1.2, py: 0.5 }}>
                    <span>{p.row.placeName || p.row.placeAddress.split(',')[0]}</span>
                    <span style={{ color: '#888', fontSize: 12 }}>{p.row.placeAddress}</span>
                </Stack>
            ),
        },
        {
            field: 'author', headerName: 'Autors', flex: 1, minWidth: 200, sortable: false,
            renderCell: (p) => {
                const name = p.row.authorName;
                const email = p.row.authorEmail;
                if (!name && !email) return <code style={{ fontSize: 12 }}>{p.row.userId}</code>;
                if (name && email) return (
                    <Stack sx={{ lineHeight: 1.2, py: 0.5 }}>
                        <span>{name}</span>
                        <span style={{ color: '#888', fontSize: 12 }}>{email}</span>
                    </Stack>
                );
                return name || email;
            },
        },
        {
            field: 'accepted', headerName: 'Statuss', width: 130, sortable: false,
            renderCell: (p) => {
                // Bez description moderācijai nav nozīmes - rāda "—" (rating-only).
                if (!p.row.description) return <span style={{ color: '#888' }}>—</span>;
                if (p.value === true)  return <Chip size="small" color="success" label="Apstiprināts" variant="outlined" />;
                if (p.value === false) return <Chip size="small" color="error"   label="Noraidīts"    variant="outlined" />;
                return <Chip size="small" color="warning" label="Gaida"        variant="outlined" />;
            },
        },
        {
            field: 'deleted', headerName: '', width: 40, sortable: false,
            renderCell: (p) => p.value
                ? <Tooltip title={`Dzēsts ${p.row.deletedAt ? new Date(p.row.deletedAt).toLocaleString('lv-LV') : ''}`}>
                      <DeleteIcon fontSize="small" color="disabled" />
                  </Tooltip>
                : null,
        },
        {
            field: 'actions', headerName: '', width: 140, sortable: false, filterable: false,
            renderCell: (p) => {
                const busy = decideMutation.isPending
                    && decideMutation.variables?.userId === p.row.userId
                    && decideMutation.variables?.placeId === p.row.placeId;
                const hasText = !!p.row.description;
                return (
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title={hasText ? 'Apstiprināt tekstu' : 'Nav teksta ko moderēt'}>
                            <span>
                                <IconButton
                                    size="small" color="success"
                                    disabled={p.row.deleted || !hasText || p.row.accepted === true || busy}
                                    onClick={() => decideMutation.mutate({
                                        userId: p.row.userId, placeId: p.row.placeId, accepted: true,
                                    })}
                                >
                                    <CheckCircleOutlineIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={hasText ? 'Noraidīt tekstu' : 'Nav teksta ko moderēt'}>
                            <span>
                                <IconButton
                                    size="small" color="warning"
                                    disabled={p.row.deleted || !hasText || p.row.accepted === false || busy}
                                    onClick={() => decideMutation.mutate({
                                        userId: p.row.userId, placeId: p.row.placeId, accepted: false,
                                    })}
                                >
                                    <HighlightOffIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Dzēst atsauksmi">
                            <span>
                                <IconButton
                                    size="small" color="error" disabled={p.row.deleted}
                                    onClick={() => setConfirmDelete(p.row)}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                );
            },
        },
    ], [decideMutation]);

    return (
        <Box>
            <Typography variant="h4" gutterBottom>Atsauksmes</Typography>

            {/* Filtri */}
            <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
                <TextField
                    size="small" label="Vietas ID (UUID)"
                    value={filters.placeId}
                    onChange={(e) => setFilters({ ...filters, placeId: e.target.value.trim() })}
                    sx={{ width: 280 }}
                />
                <TextField
                    size="small" label="Lietotāja UID"
                    value={filters.userId}
                    onChange={(e) => setFilters({ ...filters, userId: e.target.value.trim() })}
                    sx={{ width: 240 }}
                />
                <TextField
                    size="small" select label="Min. zvaigznes"
                    value={filters.minStars}
                    onChange={(e) => setFilters({ ...filters, minStars: e.target.value })}
                    sx={{ width: 140 }}
                >
                    <MenuItem value="">Visas</MenuItem>
                    <MenuItem value="1">1+</MenuItem>
                    <MenuItem value="2">2+</MenuItem>
                    <MenuItem value="3">3+</MenuItem>
                    <MenuItem value="4">4+</MenuItem>
                    <MenuItem value="5">5</MenuItem>
                </TextField>
                <TextField
                    size="small" label="Meklēt atsauksmē"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    sx={{ width: 240 }}
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={filters.includeDeleted}
                            onChange={(e) => setFilters({ ...filters, includeDeleted: e.target.checked })}
                        />
                    }
                    label="Iekļaut dzēstās"
                />
                <Button onClick={() => setFilters({
                    placeId: '', userId: '', minStars: '', search: '', includeDeleted: false,
                })}>
                    Notīrīt
                </Button>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>Neizdevās ielādēt atsauksmju sarakstu.</Alert>}

            <Box sx={{ height: 'calc(100vh - 260px)', minHeight: 400, bgcolor: 'background.paper' }}>
                <DataGrid
                    rows={reviews || []}
                    columns={columns}
                    getRowId={(r) => `${r.userId}|${r.placeId}`}
                    getRowHeight={() => 'auto'}
                    loading={isLoading}
                    disableRowSelectionOnClick
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    pageSizeOptions={[25, 50, 100]}
                />
            </Box>

            <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
                <DialogTitle>Dzēst atsauksmi?</DialogTitle>
                <DialogContent>
                    {confirmDelete && (
                        <Stack spacing={1}>
                            <Box>
                                <Rating value={confirmDelete.star} size="small" readOnly />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {confirmDelete.description || '(bez teksta)'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Autors: {confirmDelete.authorName || confirmDelete.authorEmail || confirmDelete.userId}
                            </Typography>
                            <Alert severity="info">
                                Atsauksme tiks soft-dzēsta (deleted=true). DB ieraksts paliek vēsturei.
                            </Alert>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(null)}>Atcelt</Button>
                    <Button
                        color="error" variant="contained"
                        onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete)}
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Dzēst'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
